import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { POST_BY_SLUG, POSTS } from "@/data/posts";
import PageHead from "@/components/PageHead";
import CTAStrip from "@/components/CTAStrip";
import { Reveal } from "@/components/Reveal";
import { ArrowUpRight } from "@phosphor-icons/react";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export default function BlogPost() {
  const { slug } = useParams();
  const p = POST_BY_SLUG[slug];
  if (!p) return <Navigate to="/insights" replace />;

  const others = POSTS.filter((x) => x.slug !== p.slug).slice(0, 3);

  return (
    <main data-testid={`blog-post-${p.slug}`}>
      <PageHead
        title={p.title}
        description={p.excerpt}
        path={`/insights/${p.slug}`}
        image={p.cover}
        type="article"
      />

      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-12 pt-12 md:px-12 md:pb-20 md:pt-20">
          <Reveal>
            <div className="flex items-center gap-3">
              <Link
                to="/insights"
                className="font-body text-xs uppercase tracking-[0.25em] text-citirex-sub hover:text-citirex-ink"
              >
                Insights
              </Link>
              <span className="font-body text-xs text-citirex-sub">/</span>
              <span className="font-body text-xs uppercase tracking-[0.25em] text-citirex-accent">
                {p.category}
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-8 max-w-4xl font-display text-4xl font-semibold leading-[1] tracking-tight md:text-6xl lg:text-7xl">
              {p.title}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-3xl font-editorial text-2xl italic leading-snug text-citirex-ink md:text-3xl">
              {p.excerpt}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 font-body text-sm uppercase tracking-[0.2em] text-citirex-sub">
              By {p.author} · {formatDate(p.date)} · {p.readTime}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="relative aspect-[21/9] w-full overflow-hidden">
            <img
              src={p.cover}
              alt={p.title}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-3xl px-6 py-20 md:px-0 md:py-28">
          <article className="space-y-6">
            {p.body.map((para, i) => (
              <p
                key={i}
                className={
                  i === 0
                    ? "font-editorial text-2xl italic leading-snug text-citirex-ink md:text-3xl"
                    : "font-body text-lg leading-relaxed text-citirex-ink md:text-xl"
                }
              >
                {i !== 0 && (
                  <span className="float-left mr-3 mt-1 inline-block h-3 w-3 bg-citirex-accent" />
                )}
                {para}
              </p>
            ))}
          </article>
        </div>
      </section>

      {others.length > 0 && (
        <section className="border-t border-citirex-border bg-citirex-bg">
          <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
            <p className="overline">Keep reading</p>
            <div className="mt-12 grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-3">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/insights/${o.slug}`}
                  data-testid={`blog-other-${o.slug}`}
                  className="group flex flex-col bg-citirex-bg p-8 transition-colors hover:bg-citirex-surface"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-body text-[10px] uppercase tracking-[0.25em] text-citirex-accent">
                      {o.category}
                    </span>
                    <ArrowUpRight
                      size={18}
                      weight="bold"
                      className="transition-transform group-hover:rotate-45"
                    />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-medium text-citirex-ink md:text-2xl">
                    {o.title}
                  </h3>
                  <p className="mt-3 font-body text-sm text-citirex-sub">
                    {formatDate(o.date)} · {o.readTime}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTAStrip
        eyebrow="Read it. Use it."
        title="Hire the team that writes the playbook."
        buttonText="Talk to sales"
      />
    </main>
  );
}
